import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Anchor, LogOut, LayoutDashboard, Users, Layers, Lightbulb, Settings, Search as SearchIcon,
  TrendingUp, Mail, Phone, MapPin, Calendar, Wallet, X, Save, Lock, Download, BarChart3, Sparkles,
  Trash2, AlertTriangle, Database, Wrench, Globe, Send,
} from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  STATUS_META, PROBABILITY_META, BOAT_LABELS, ACCOM_LABELS, GROUP_LABELS, EXP_LABELS,
  fmtEur, fmtDate,
} from "@/admin/adminLabels";
import NotificationsBell from "@/admin/NotificationsBell";
import MarketPanel from "@/admin/MarketPanel";
import ActivityTimeline from "@/admin/ActivityTimeline";
import CompatibleLeadsPanel from "@/admin/CompatibleLeadsPanel";
import ContactMessagesPanel from "@/admin/ContactMessagesPanel";
import OverviewOperative from "@/admin/OverviewOperative";
import ProposalsSection from "@/admin/ProposalsSection";
import ClustersPanelV2 from "@/admin/ClustersPanelV2";
import { formatPeriod } from "@/admin/adminLabels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function downloadAuth(url, filename) {
  const tk = localStorage.getItem("sm_admin_token");
  return fetch(url, { headers: { Authorization: `Bearer ${tk}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(u);
    });
}

function scoreBadge(score) {
  if (score == null) return null;
  if (score >= 75) return { label: "🟢 Alta", className: "bg-green-100 text-green-900 border-green-300" };
  if (score >= 50) return { label: "🟡 Media", className: "bg-yellow-100 text-yellow-900 border-yellow-300" };
  return { label: "🔴 Bassa", className: "bg-red-100 text-red-900 border-red-300" };
}

const STATUSES = Object.keys(STATUS_META);

export default function AdminDashboard() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  if (user === false) return <Navigate to="/admin" replace />;
  if (!user) return <div className="min-h-screen pt-32 text-center text-ink">Loading…</div>;

  const onLogout = () => {
    logout();
    navigate("/admin");
  };

  const onSelectLead = (leadId) => {
    setActive("leads");
    setSelectedLeadId(leadId);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAF9]">
      {/* Topbar */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full flex items-center justify-center overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_charter-connect-beta/artifacts/9lb67pg5_logo%20seamatch.png" alt="Seamatch" className="h-9 w-9 object-cover" />
            </span>
            <div>
              <p className="font-serif text-lg text-navy leading-none">Seamatch Admin</p>
              <p className="text-[11px] uppercase tracking-wider text-ink">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell onSelectLead={onSelectLead} />
            <Button
              data-testid="admin-logout"
              variant="outline"
              onClick={onLogout}
              className="border-navy/20 text-navy hover:bg-sand h-9"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
        <Tabs value={active} onValueChange={setActive}>
          <TabsList data-testid="admin-tabs" className="bg-white border border-[#E2E8F0] p-1 h-auto flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> Panoramica
            </TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">
              <Users className="h-4 w-4 mr-1.5" /> Richieste
            </TabsTrigger>
            <TabsTrigger value="market" data-testid="tab-market">
              <BarChart3 className="h-4 w-4 mr-1.5" /> Domanda di mercato
            </TabsTrigger>
            <TabsTrigger value="clusters" data-testid="tab-clusters">
              <Layers className="h-4 w-4 mr-1.5" /> Cluster
            </TabsTrigger>
            <TabsTrigger value="suggestions" data-testid="tab-suggestions">
              <Lightbulb className="h-4 w-4 mr-1.5" /> Suggerimenti
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              <Mail className="h-4 w-4 mr-1.5" /> Messaggi
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-1.5" /> Impostazioni
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6"><OverviewOperative onSelectLead={onSelectLead} /></TabsContent>
          <TabsContent value="leads" className="mt-6"><LeadsPanel selectedLeadId={selectedLeadId} onSelectionConsumed={() => setSelectedLeadId(null)} /></TabsContent>
          <TabsContent value="market" className="mt-6"><MarketPanel onSelectLead={onSelectLead} /></TabsContent>
          <TabsContent value="clusters" className="mt-6"><ClustersPanelV2 onSelectLead={onSelectLead} /></TabsContent>
          <TabsContent value="suggestions" className="mt-6"><SuggestionsPanel /></TabsContent>
          <TabsContent value="messages" className="mt-6"><ContactMessagesPanel /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ========== OVERVIEW ==========
function OverviewPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-ink">Caricamento…</div>;

  const g = stats.general, c = stats.commercial, e = stats.economic;

  const kpiBlocks = [
    { label: "Richieste totali", value: g.total_requests, icon: Users },
    { label: "Preventivi inviati", value: c.quotes_sent, icon: Mail },
    { label: "Trattative aperte", value: c.open_negotiations, icon: TrendingUp },
    { label: "Conferme", value: c.confirmed_bookings, icon: Anchor },
    { label: "Conversion rate", value: `${c.conversion_rate}%`, icon: TrendingUp },
  ];

  const econKpis = [
    { label: "Pipeline aperta (stimato)", value: fmtEur(e.pipeline_value), hint: "Valore stimato dei lead aperti" },
    { label: "Commissione potenziale", value: fmtEur(e.pipeline_commission), hint: "10% stimato sui lead aperti" },
    { label: "Fatturato totale", value: fmtEur(e.revenue_total) },
    { label: "Commissioni stimate", value: fmtEur(e.commission_estimated_total) },
    { label: "Commissioni incassate", value: fmtEur(e.commission_collected_total) },
    { label: "Valore medio prenotazione", value: fmtEur(e.avg_booking_value) },
    { label: "Commissione media", value: fmtEur(e.avg_commission) },
  ];

  return (
    <div className="space-y-8">
      {/* Commercial KPIs */}
      <section>
        <p className="label-eyebrow">Dashboard commerciale</p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {kpiBlocks.map((k, i) => (
            <div key={i} data-testid={`kpi-${i}`} className="bg-white rounded-xl border border-[#E2E8F0] p-5">
              <div className="flex items-center gap-2"><k.icon className="h-4 w-4 text-turquoise" /><span className="text-[11px] uppercase tracking-wider text-ink">{k.label}</span></div>
              <p className="mt-3 font-serif text-3xl text-navy">{k.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Economic KPIs */}
      <section>
        <p className="label-eyebrow">Dashboard economica</p>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {econKpis.map((k, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] p-5">
              <p className="text-[11px] uppercase tracking-wider text-ink">{k.label}</p>
              <p className="mt-3 font-serif text-2xl text-navy">{k.value}</p>
              {k.hint && <p className="text-[10px] text-ink mt-1">{k.hint}</p>}
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-ink">{e.confirmed_count} prenotazioni confermate · pipeline = valore stimato lead non chiusi</p>
      </section>

      {/* Distributions */}
      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <DistroCard title="Richieste per stato" entries={Object.entries(g.by_status)} resolver={(k) => STATUS_META[k]?.label || k} />
        <DistroCard title="Per destinazione" entries={g.by_destination} />
        <DistroCard title="Per periodo" entries={g.by_period} />
        <DistroCard title="Per budget" entries={g.by_budget} />
        <DistroCard title="Per tipo barca" entries={g.by_boat_type} resolver={(k) => BOAT_LABELS[k] || k} />
        <DistroCard title="Per esperienza" entries={g.by_experience} resolver={(k) => EXP_LABELS[k] || k} />
      </section>
    </div>
  );
}

function DistroCard({ title, entries, resolver }) {
  const total = entries.reduce((acc, [, v]) => acc + v, 0) || 1;
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
      <p className="font-medium text-navy">{title}</p>
      <ul className="mt-4 space-y-2.5">
        {entries.slice(0, 8).map(([k, v]) => (
          <li key={k} className="flex items-center gap-3">
            <span className="text-sm text-navy w-1/3 truncate">{resolver ? resolver(k) : k}</span>
            <div className="flex-1 h-1.5 bg-sand rounded-full overflow-hidden">
              <div className="h-full bg-navy" style={{ width: `${(v / total) * 100}%` }} />
            </div>
            <span className="text-sm font-semibold text-navy w-8 text-right">{v}</span>
          </li>
        ))}
        {entries.length === 0 && <li className="text-sm text-ink">Nessun dato</li>}
      </ul>
    </div>
  );
}

// ========== LEADS LIST + DETAIL ==========
function LeadsPanel({ selectedLeadId, onSelectionConsumed }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", q: "" });
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "all") params.status = filters.status;
      if (filters.q) params.q = filters.q;
      const res = await api.get("/admin/leads", { params });
      setLeads(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status]);

  // If notified from outside, open the selected lead
  useEffect(() => {
    if (!selectedLeadId) return;
    (async () => {
      try {
        const r = await api.get(`/admin/leads/${selectedLeadId}`);
        setSelected(r.data);
      } catch (e) { /* noop */ }
      onSelectionConsumed?.();
    })();
  }, [selectedLeadId, onSelectionConsumed]);

  const exportCsv = () => {
    const u = new URL(`${BACKEND_URL}/api/admin/export/leads.csv`);
    if (filters.status !== "all") u.searchParams.set("status", filters.status);
    downloadAuth(u.toString(), "seamatch_leads.csv");
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink">Cerca</Label>
            <div className="mt-1 flex gap-2">
              <Input
                data-testid="leads-search"
                placeholder="Nome, email, telefono…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="h-10 w-64"
              />
              <Button data-testid="leads-search-btn" onClick={load} variant="outline" className="border-navy/20 text-navy hover:bg-sand">
                <SearchIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink">Stato</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="leads-status-filter" className="mt-1 h-10 w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-ink">{leads.length} richieste</p>
          <Button data-testid="export-leads" onClick={exportCsv} variant="outline" className="border-navy/20 text-navy hover:bg-sand">
            <Download className="h-4 w-4 mr-2" /> Esporta CSV
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sand">
            <tr className="text-left">
              {["Cliente", "Score", "Budget", "Persone", "Mete", "Tipo barca", "Periodo / date", "Stato", "Data ricezione", ""].map((h) => (
                <th key={h} className="p-3 text-[11px] uppercase tracking-wider text-ink">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="p-8 text-center text-ink">Caricamento…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-ink">Nessuna richiesta trovata.</td></tr>
            )}
            {leads.map((l) => {
              const status = STATUS_META[l.status || "nuova_richiesta"];
              const sb = scoreBadge(l.auto_score);
              const period = formatPeriod(l);
              return (
                <tr
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  onClick={() => setSelected(l)}
                  className="border-t border-[#E2E8F0] hover:bg-sand/40 cursor-pointer"
                >
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      {l.da_contattare_oggi && <span title="Da contattare oggi" className="h-2 w-2 rounded-full bg-gold shrink-0" />}
                      <p className="font-medium text-navy">{l.name}</p>
                    </div>
                    <p className="text-xs text-ink truncate max-w-[200px]">{l.email}</p>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    {sb ? (
                      <span className={`chip border text-[11px] ${sb.className}`} title={`Score ${l.auto_score} · compatibilità con budget, meta, periodo, persone e barca`}>
                        {sb.label} <span className="opacity-60 ml-1">{l.auto_score}</span>
                      </span>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-navy whitespace-nowrap">{fmtEur(l.budget)}</td>
                  <td className="p-3 text-navy">{l.num_participants}</td>
                  <td className="p-3 text-ink text-xs">{(l.destinations || []).join(" / ") || "—"}</td>
                  <td className="p-3 text-ink text-xs">{BOAT_LABELS[l.boat_type] || l.boat_type || "—"}</td>
                  <td className="p-3 text-xs">
                    <span
                      className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: period.badgeBg, color: period.badgeColor }}
                    >
                      {period.badge}
                    </span>
                    {period.lines.length > 0 && (
                      <div className="mt-1 text-ink/70 leading-tight">
                        {period.lines.map((l2, i) => <div key={i}>{l2}</div>)}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="chip border-0 text-[11px]" style={{ background: status.bg, color: status.color }}>
                      {status.label}
                    </span>
                  </td>
                  <td className="p-3 text-ink text-xs whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="p-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      data-testid={`delete-lead-row-${l.id}`}
                      title="Elimina richiesta"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm("Sei sicura di voler eliminare questa richiesta? L'operazione non può essere annullata.")) return;
                        try {
                          await api.delete(`/admin/leads/${l.id}`);
                          setLeads((arr) => arr.filter((x) => x.id !== l.id));
                          toast.success("Richiesta eliminata");
                        } catch {
                          toast.error("Errore eliminazione");
                        }
                      }}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-transparent hover:border-red-300 hover:bg-red-50 text-ink hover:text-red-600 transition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <LeadDetailDialog lead={selected} onClose={() => setSelected(null)} onSaved={(updated) => {
        setLeads((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
        setSelected(updated);
      }} onDeleted={(deletedId) => {
        setLeads((arr) => arr.filter((x) => x.id !== deletedId));
        setSelected(null);
      }} />
    </div>
  );
}

/* ============= REALISTIC RANGE ============= */
function RealisticRange({ leadId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!leadId) return;
    let cancel = false;
    api.get(`/admin/leads/${leadId}/realistic-range`)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [leadId]);
  if (!data) return null;
  return (
    <div className="mt-3 p-3 rounded-lg bg-sand/40 border border-[#E5E1D2]" data-testid="realistic-range">
      <p className="text-[10px] uppercase tracking-wider text-gold font-semibold">Range realistico per questa richiesta</p>
      <p className="mt-1 font-serif text-base text-navy">{fmtEur(data.realistic_low)} – {fmtEur(data.realistic_high)}</p>
      <p className="text-[11px] text-ink/60 mt-0.5">
        ({fmtEur(data.realistic_low_per_person)} – {fmtEur(data.realistic_high_per_person)} a persona)
      </p>
      <p className="mt-1.5 text-[10px] text-ink/50">
        Calcolato su: {BOAT_LABELS[data.factors.boat_type] || data.factors.boat_type} · {data.factors.month || "periodo n.d."} · {data.factors.duration} · {(data.factors.destinations || []).join(", ") || "destinazioni n.d."} {data.factors.skipper ? "· + skipper" : ""}
      </p>
    </div>
  );
}


function LeadDetailDialog({ lead, onClose, onSaved, onDeleted }) {
  const [edit, setEdit] = useState(null);
  const [matches, setMatches] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (lead) {
      setEdit({
        status: lead.status || "nuova_richiesta",
        probability: lead.probability || "",
        internal_notes: lead.internal_notes || "",
        commission_eur: lead.commission_eur ?? "",
        da_contattare_oggi: !!lead.da_contattare_oggi,
        booking_value: lead.booking_value ?? "",
        prev_status: lead.status || "nuova_richiesta",  // tracking
      });
      api.get(`/admin/leads/${lead.id}/matches`).then((r) => setMatches(r.data)).catch(() => setMatches(null));
    } else {
      setMatches(null); setEdit(null);
    }
  }, [lead]);

  if (!lead || !edit) return null;

  // Quando l'utente passa a "prenotazione_confermata" mostriamo il form valore pratica
  const showBookingValueForm = edit.status === "prenotazione_confermata";

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        status: edit.status,
        internal_notes: edit.internal_notes,
      };
      if (edit.probability) body.probability = edit.probability;

      // Logica conferma prenotazione: se status = prenotazione_confermata e booking_value compilato,
      // calcola commissione = 8% × booking_value (a meno che commission_eur non sia override manuale)
      if (showBookingValueForm) {
        if (edit.booking_value !== "" && edit.booking_value != null) {
          const bv = parseFloat(edit.booking_value);
          body.booking_value = bv;
          // Se commission_eur non è stato sovrascritto manualmente, ricalcola 8%
          if (edit.commission_eur === "" || edit.commission_eur == null) {
            body.commission_eur = Math.round(bv * 0.08 * 100) / 100;
          } else {
            body.commission_eur = parseFloat(edit.commission_eur);
          }
        } else {
          toast.error("Inserisci il valore pratica per confermare la prenotazione");
          setSaving(false);
          return;
        }
      } else if (edit.commission_eur !== "" && edit.commission_eur != null) {
        body.commission_eur = parseFloat(edit.commission_eur);
      } else {
        body.commission_eur = null;
      }

      const res = await api.patch(`/admin/leads/${lead.id}`, body);
      toast.success(showBookingValueForm ? "Prenotazione confermata · Fatturato aggiornato" : "Richiesta aggiornata");
      onSaved?.(res.data);
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleTodo = async () => {
    try {
      const res = await api.patch(`/admin/leads/${lead.id}/todo`, {
        da_contattare_oggi: !edit.da_contattare_oggi,
      });
      setEdit({ ...edit, da_contattare_oggi: res.data.da_contattare_oggi });
      onSaved?.(res.data);
      toast.success(res.data.da_contattare_oggi ? "Aggiunta a 'Da contattare oggi'" : "Rimossa da 'Da contattare oggi'");
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-detail-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy">{lead.name}</DialogTitle>
          <p className="text-sm text-ink">Ricevuta {fmtDate(lead.created_at)} · ID {lead.id.slice(0, 8).toUpperCase()}</p>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-2">
          {/* LEFT: customer data */}
          <div className="space-y-4">
            <Section title="Contatti">
              <KV icon={Mail} label="Email" value={lead.email} />
              <KV icon={Phone} label="Telefono / WhatsApp" value={(lead.phone_country_code ? `${lead.phone_country_code} ` : "") + (lead.phone || "—")} />
              {(lead.language || lead.phone_country || lead.phone_country_code) && (
                <KV icon={Globe} label="Lingua / Paese" value={
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-navy text-white">{(lead.language || "it").toUpperCase()}</span>
                    {(lead.phone_country || lead.phone_country_code) && <span className="text-xs text-ink">{lead.phone_country || lead.phone_country_code}</span>}
                  </span>
                } />
              )}
            </Section>

            <Section title="Richiesta">
              <KV icon={Wallet} label="Budget" value={fmtEur(lead.budget)} />
              <KV icon={Users} label="Persone" value={`${lead.num_participants} (${GROUP_LABELS[lead.group_type] || lead.group_type})`} />
              <KV icon={Anchor} label="Sistemazione" value={ACCOM_LABELS[lead.accommodation] || lead.accommodation} />
              <KV icon={Anchor} label="Tipo barca" value={BOAT_LABELS[lead.boat_type] || lead.boat_type} />
              <KV icon={MapPin} label="Mete (in ordine)" value={(lead.destinations || []).join(" › ") || "—"} />
              {/* Period with badge — coerent with questionnaire */}
              {(() => {
                const period = formatPeriod(lead);
                return (
                  <div className="flex items-start gap-2.5">
                    <Calendar className="h-4 w-4 text-turquoise mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-wider text-ink">Periodo</p>
                      <span
                        className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full mt-1"
                        style={{ background: period.badgeBg, color: period.badgeColor }}
                      >
                        {period.badge}
                      </span>
                      {period.lines.map((ln, i) => (
                        <p key={i} className="text-sm text-navy mt-0.5">{ln}</p>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <KV icon={Anchor} label="Skipper" value={lead.skipper_needed === "yes" ? "Sì" : lead.skipper_needed === "no" ? "No" : "Da consigliare"} />
              {lead.experiences?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {lead.experiences.map((e) => (
                    <span key={e} className="chip border-navy/20 text-navy bg-white text-[11px]">{EXP_LABELS[e] || e}</span>
                  ))}
                </div>
              )}
            </Section>

            {/* Auto score & estimated value */}
            <Section title="Scoring automatico">
              <div className="flex items-center gap-3">
                {(() => {
                  const sb = scoreBadge(lead.auto_score);
                  return sb ? (
                    <span data-testid="auto-score-badge" className={`chip border text-xs ${sb.className}`}>
                      {sb.label} <span className="opacity-60 ml-1">{lead.auto_score}/100</span>
                    </span>
                  ) : <span className="text-sm text-ink">—</span>;
                })()}
                <span className="text-xs text-ink">Calcolato su completezza, realismo, flessibilità e compatibilità</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink">Budget complessivo</p>
                  <p className="font-serif text-lg text-navy">{fmtEur(lead.budget)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-ink">Budget pro quota</p>
                  <p className="font-serif text-lg text-navy">
                    {lead.num_participants > 0 ? `${fmtEur(Math.round(lead.budget / lead.num_participants))}/persona` : "—"}
                  </p>
                </div>
              </div>
              <RealisticRange leadId={lead.id} />
              {lead.auto_score_reasons?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-ink cursor-pointer">Vedi i fattori usati</summary>
                  <ul className="mt-2 space-y-1 text-[11px] text-ink">
                    {lead.auto_score_reasons.map((r, i) => <li key={i}>· {r}</li>)}
                  </ul>
                </details>
              )}
            </Section>

            {/* Lead source */}
            {lead.lead_source && Object.keys(lead.lead_source).length > 0 && (
              <Section title="Origine richiesta">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {["channel", "utm_source", "utm_medium", "utm_campaign", "referrer"].map((k) => (
                    lead.lead_source[k] ? (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-wider text-ink">{k}</p>
                        <p className="text-navy break-words">{lead.lead_source[k]}</p>
                      </div>
                    ) : null
                  ))}
                </div>
              </Section>
            )}

            <CompatibleLeadsPanel leadId={lead.id} />
          </div>

          {/* RIGHT: editable admin fields */}
          <div className="space-y-4">
            <Section title="Gestione richiesta">
              {/* Da contattare oggi toggle */}
              <button
                type="button"
                data-testid="toggle-todo"
                onClick={toggleTodo}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition ${
                  edit.da_contattare_oggi
                    ? "border-gold bg-gold/15 text-navy"
                    : "border-[#E2E8F0] bg-white text-ink hover:border-navy/30"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span className={`h-2.5 w-2.5 rounded-full ${edit.da_contattare_oggi ? "bg-gold" : "bg-ink/30"}`} />
                  Da contattare oggi
                </span>
                <span className="text-xs">{edit.da_contattare_oggi ? "ATTIVO" : "Click per attivare"}</span>
              </button>

              <div>
                <Label className="text-xs uppercase tracking-wider text-ink">Stato</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger data-testid="edit-status" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Inline form valore pratica quando confermi prenotazione */}
              {showBookingValueForm && (
                <div data-testid="booking-value-form" className="rounded-lg border-2 border-gold/40 bg-gold/10 p-4 space-y-2">
                  <p className="text-[11px] uppercase tracking-wider text-gold font-bold flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold inline-block" />
                    Conferma prenotazione
                  </p>
                  <p className="text-xs text-ink leading-relaxed">
                    Indica il <strong>valore definitivo della pratica</strong> (booking value). La commissione Seamatch è calcolata automaticamente all'<strong>8%</strong>.
                  </p>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-navy">Valore pratica € *</Label>
                    <Input
                      data-testid="edit-booking-value"
                      type="number"
                      step="0.01"
                      value={edit.booking_value}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Auto-aggiorna commissione preview se l'utente non l'ha sovrascritta manualmente
                        const auto = v !== "" ? Math.round(parseFloat(v) * 0.08 * 100) / 100 : "";
                        setEdit(prev => ({
                          ...prev,
                          booking_value: v,
                          commission_eur: prev.commission_eur === "" || prev.commission_eur == null ? auto : prev.commission_eur,
                        }));
                      }}
                      placeholder="es. 5800"
                      className="mt-1 h-10 bg-white"
                    />
                  </div>
                  {edit.booking_value !== "" && !isNaN(parseFloat(edit.booking_value)) && (
                    <p className="text-sm text-navy font-medium">
                      → Commissione 8%: <strong>{fmtEur(Math.round(parseFloat(edit.booking_value) * 0.08 * 100) / 100)}</strong>
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-wider text-ink">Note interne</Label>
                <Textarea
                  data-testid="edit-notes"
                  rows={4}
                  value={edit.internal_notes}
                  onChange={(e) => setEdit({ ...edit, internal_notes: e.target.value })}
                  placeholder="Es. società charter contattata, promemoria, esito chiamata…"
                  className="mt-1"
                />
              </div>
            </Section>

            <Section title="Commissione (visibile solo a te)">
              <p className="text-[11px] text-ink/60">
                Se la richiesta è confermata, alimenta il <strong>Fatturato realizzato</strong>.<br />
                Se è in pipeline (preventivi inviati / cliente interessato / trattativa), alimenta il <strong>Fatturato potenziale</strong>.<br />
                Lascia vuoto per calcolo automatico all'<strong>8%</strong> del valore pratica.
              </p>
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink">Commissione attesa (€) — override manuale</Label>
                <Input
                  data-testid="edit-commission"
                  type="number"
                  step="0.01"
                  value={edit.commission_eur}
                  onChange={(e) => setEdit({ ...edit, commission_eur: e.target.value })}
                  placeholder="vuoto = 8% auto"
                  className="mt-1 h-10"
                />
              </div>
            </Section>

            <Button data-testid="save-lead" onClick={save} disabled={saving} className="w-full bg-navy text-white hover:bg-[#06101f] h-11">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Salva modifiche"}
            </Button>

            {/* Email manuale: stiamo costruendo il match perfetto */}
            <Button
              data-testid="send-match-building-email"
              type="button"
              variant="outline"
              onClick={async () => {
                const lang = (lead.language || "it").toUpperCase();
                if (!window.confirm(`Inviare al cliente l'email "Stiamo costruendo il match perfetto" in ${lang}?`)) return;
                try {
                  const r = await api.post(`/admin/leads/${lead.id}/emails/match-in-building`);
                  toast.success(`Email match in costruzione inviata in ${r.data.lang.toUpperCase()}`);
                  // Recarica i dati del lead per aggiornare la timeline
                  const upd = await api.get(`/admin/leads/${lead.id}`);
                  onSaved?.(upd.data);
                } catch {
                  toast.error("Errore invio email");
                }
              }}
              className="w-full border-turquoise/40 text-turquoise hover:bg-turquoise/5 h-10"
            >
              <Send className="h-4 w-4 mr-2" /> Invia email "match in costruzione" ({(lead.language || "it").toUpperCase()})
            </Button>

            <Button
              data-testid="delete-lead-detail"
              type="button"
              variant="outline"
              disabled={deleting}
              onClick={async () => {
                if (!window.confirm("Sei sicura di voler eliminare questa richiesta? L'operazione non può essere annullata.")) return;
                setDeleting(true);
                try {
                  await api.delete(`/admin/leads/${lead.id}`);
                  toast.success("Richiesta eliminata");
                  onDeleted?.(lead.id);
                } catch {
                  toast.error("Errore eliminazione");
                } finally {
                  setDeleting(false);
                }
              }}
              className="w-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 h-10"
            >
              <Trash2 className="h-4 w-4 mr-2" /> {deleting ? "Eliminazione…" : "Elimina richiesta"}
            </Button>

            <details className="rounded-xl border border-[#E2E8F0] bg-white" data-testid="activity-timeline-accordion">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-navy hover:bg-sand/40 select-none flex items-center justify-between">
                <span>Apri timeline attività ({(lead.activities || []).length})</span>
                <span className="text-xs text-ink/60">click per espandere</span>
              </summary>
              <div className="border-t border-[#E2E8F0]">
                <ActivityTimeline
                  leadId={lead.id}
                  activities={lead.activities || []}
                  onChanged={async () => {
                    try {
                      const r = await api.get(`/admin/leads/${lead.id}`);
                      onSaved?.(r.data);
                    } catch (e) { /* noop */ }
                  }}
                />
              </div>
            </details>
          </div>
        </div>

        {/* Proposals section — full width */}
        <div className="mt-6 border-t border-[#E2E8F0] pt-6">
          <ProposalsSection lead={lead} onLeadUpdated={(updated) => onSaved?.(updated)} />
        </div>

        {/* Suggested matches */}
        {matches && (
          <div className="mt-6 border-t border-[#E2E8F0] pt-6">
            <p className="label-eyebrow">Suggerimenti dall&apos;algoritmo</p>
            <h3 className="mt-2 font-serif text-xl text-navy">Match consigliati per questa richiesta</h3>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {[...matches.best_matches, ...matches.near_matches].slice(0, 4).map((m) => (
                <div key={m.offer.id} data-testid={`match-${m.offer.id}`} className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-sand/40">
                  <img src={m.offer.image_url} alt="" className="h-16 w-20 rounded-md object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy truncate">{m.offer.name}</p>
                    <p className="text-xs text-ink truncate">{m.offer.destination} · {m.offer.area} · {fmtEur(m.offer.total_price)}</p>
                    <p className="text-xs text-ink mt-0.5">{m.offer.charter_company}</p>
                  </div>
                  <Badge className="bg-navy text-white whitespace-nowrap">{m.score}%</Badge>
                </div>
              ))}
              {matches.smart_alternatives.slice(0, 2).map((a, i) => (
                <div key={i} className="p-3 rounded-lg border border-turquoise/40 bg-white">
                  <p className="text-xs uppercase tracking-wider text-turquoise font-semibold">Alternativa intelligente</p>
                  <p className="mt-1 font-medium text-navy text-sm">{a.title}</p>
                  <p className="mt-1 text-xs text-ink">{a.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-4 space-y-2.5">
      <p className="text-xs uppercase tracking-wider text-ink font-semibold">{title}</p>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function KV({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 text-turquoise mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-ink">{label}</p>
        <p className="text-sm text-navy break-words">{value}</p>
      </div>
    </div>
  );
}

function MoneyField({ label, testId, value, onChange }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink">{label}</Label>
      <Input
        data-testid={testId}
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-10"
      />
    </div>
  );
}

// ========== CLUSTERS ==========
function ClustersPanel() {
  const [clusters, setClusters] = useState(null);
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/admin/clusters").then((r) => setClusters(r.data)),
      api.get("/admin/compatible-groups").then((r) => setGroups(r.data)),
    ]).catch(() => {});
  }, []);

  if (!clusters) return <div className="text-ink">Caricamento…</div>;

  return (
    <div className="space-y-8">
      <section>
        <p className="label-eyebrow">Combinazioni cabine / gruppi</p>
        <h2 className="mt-2 font-serif text-2xl text-navy">Possibili gruppi compatibili</h2>
        <p className="mt-1 text-sm text-ink">Più richieste con stessa meta + periodo che potrebbero condividere una barca.</p>
        <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.length === 0 && <p className="text-ink text-sm">Nessuna combinazione individuata al momento.</p>}
          {groups.map((g, i) => (
            <div key={i} data-testid={`group-${i}`} className="rounded-xl bg-sand border border-[#E5E1D2] p-5">
              <p className="font-serif text-xl text-navy">{g.destination}</p>
              <p className="text-xs text-ink">{g.period}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-navy">
                <span className="chip bg-white border-navy/15">{g.leads_count} richieste</span>
                <span className="chip bg-white border-navy/15">{g.total_people} persone tot</span>
                <span className="chip bg-white border-navy/15">~ {fmtEur(g.avg_budget_per_person)}/pax</span>
              </div>
              <div className="mt-3 text-xs text-ink">{g.lead_names.join(", ")}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <DistroCard title="Per destinazione" entries={clusters.by_destination} />
        <DistroCard title="Per periodo" entries={clusters.by_period} />
        <DistroCard title="Per budget" entries={clusters.by_budget} />
        <DistroCard title="Per tipo barca" entries={clusters.by_boat_type} resolver={(k) => BOAT_LABELS[k] || k} />
        <DistroCard title="Per esperienza" entries={clusters.by_experience} resolver={(k) => EXP_LABELS[k] || k} />
      </section>
    </div>
  );
}

// ========== SUGGESTIONS ==========
function SuggestionsPanel() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/suggestions").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list) return <div className="text-ink">Caricamento…</div>;
  return (
    <div>
      <p className="label-eyebrow">Suggerimenti operativi</p>
      <h2 className="mt-2 font-serif text-2xl text-navy">Azioni che ti consigliamo oggi</h2>
      <div className="mt-5 grid md:grid-cols-2 gap-4">
        {list.length === 0 && <p className="text-ink text-sm">Nessun suggerimento al momento. Tornano quando arrivano nuove richieste.</p>}
        {list.map((s, i) => (
          <div key={i} data-testid={`sugg-${i}`} className="rounded-xl bg-white border border-[#E2E8F0] p-5">
            <p className="text-xs uppercase tracking-wider text-gold font-semibold">{s.kind.replace(/_/g, " ")}</p>
            <p className="mt-2 font-serif text-lg text-navy">{s.title}</p>
            <p className="mt-1 text-sm text-ink">{s.body}</p>
            <p className="mt-3 text-xs text-ink">{s.lead_ids?.length || 0} richieste collegate</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========== SETTINGS ==========
function SettingsPanel() {
  const { user } = useAdminAuth();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const change = async () => {
    if (pw.next.length < 8) return toast.error("Min 8 caratteri");
    if (pw.next !== pw.confirm) return toast.error("Le nuove password non coincidono");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password aggiornata");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-5">
      <div className="rounded-xl bg-white border border-[#E2E8F0] p-6">
        <p className="label-eyebrow">Account</p>
        <p className="mt-2 font-serif text-xl text-navy">{user.email}</p>
        <p className="mt-1 text-xs text-ink">Ruolo: {user.role}</p>
      </div>
      <div className="rounded-xl bg-white border border-[#E2E8F0] p-6">
        <p className="label-eyebrow">Cambia password</p>
        <div className="mt-3 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink">Password attuale</Label>
            <Input data-testid="pw-current" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="mt-1 h-10" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink">Nuova password</Label>
            <Input data-testid="pw-next" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="mt-1 h-10" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink">Conferma</Label>
            <Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1 h-10" />
          </div>
          <Button data-testid="pw-submit" onClick={change} disabled={loading} className="bg-navy text-white hover:bg-[#06101f] h-11">
            <Lock className="h-4 w-4 mr-2" /> {loading ? "…" : "Aggiorna password"}
          </Button>
        </div>
      </div>
    </div>
    <MaintenancePanel />
    </div>
  );
}

// ========== MAINTENANCE PANEL ==========
function MaintenancePanel() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/env-info");
      setInfo(r.data);
    } catch {
      setInfo(null);
    }
  };

  useEffect(() => { refresh(); }, []);

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const r = await fn();
      toast.success(r);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(null);
    }
  };

  const isProd = info?.public_site_url?.includes("seamatch.it");
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const isPreviewApi = apiBase.includes("preview.emergentagent.com");

  return (
    <div className="rounded-xl bg-white border-2 border-orange-200 p-6 space-y-5">
      <div className="flex items-start gap-3">
        <Wrench className="h-5 w-5 text-orange-600 mt-1 shrink-0" />
        <div>
          <p className="label-eyebrow text-orange-700">Manutenzione &amp; Ambiente</p>
          <h3 className="mt-1 font-serif text-xl text-navy">Diagnostica e pulizia archivio</h3>
          <p className="mt-1 text-xs text-ink">
            Strumenti per verificare quale ambiente/database stai usando ed effettuare reset/seed dei dati demo.
          </p>
        </div>
      </div>

      {/* DIAGNOSI AMBIENTE */}
      <div className="rounded-lg bg-sand/60 border border-[#E5E1D2] p-4 space-y-2">
        <p className="text-[11px] uppercase tracking-wider text-navy font-semibold flex items-center gap-1.5">
          <Database className="h-3.5 w-3.5" /> Ambiente corrente (server)
        </p>
        {info ? (
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink">
            <div><span className="text-ink/60">Sito pubblico:</span> <span className="text-navy font-medium" data-testid="env-public-url">{info.public_site_url || "—"}</span></div>
            <div><span className="text-ink/60">DB name:</span> <span className="text-navy font-medium">{info.db_name}</span></div>
            <div><span className="text-ink/60">Email mittente:</span> <span className="text-navy font-medium">{info.sender_email || "—"}</span></div>
            <div><span className="text-ink/60">Email admin:</span> <span className="text-navy font-medium">{info.admin_email || "—"}</span></div>
            <div><span className="text-ink/60">Resend attivo:</span> <span className="text-navy font-medium">{info.resend_configured ? "Sì" : "NO"}</span></div>
            <div><span className="text-ink/60">Richieste in DB:</span> <span className="text-navy font-bold" data-testid="env-leads-count">{info.counts.leads}</span></div>
            <div><span className="text-ink/60">Preventivi in DB:</span> <span className="text-navy font-medium">{info.counts.proposals}</span></div>
            <div><span className="text-ink/60">KB docs:</span> <span className="text-navy font-medium">{info.counts.kb_docs}</span></div>
          </div>
        ) : (
          <p className="text-xs text-ink">Caricamento…</p>
        )}
        <div className="mt-2 text-[11px] leading-relaxed">
          <p><span className="text-ink/60">API in uso dal browser:</span> <span className={`font-mono ${isPreviewApi ? "text-orange-600" : "text-green-700"}`}>{apiBase}</span></p>
          {isPreviewApi && (
            <div className="mt-2 rounded-md bg-orange-50 border border-orange-200 p-2.5 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
              <p className="text-orange-800">
                <strong>Sei nell'ambiente PREVIEW</strong> (sviluppo). Le richieste arrivate da <strong>seamatch.it</strong> sono in produzione, non qui.
                Per gestirle apri <a href={`${info?.public_site_url || "https://seamatch.it"}/admin`} target="_blank" rel="noreferrer" className="underline">{(info?.public_site_url || "https://seamatch.it") + "/admin"}</a>.
              </p>
            </div>
          )}
          {!isPreviewApi && isProd && (
            <div className="mt-2 rounded-md bg-green-50 border border-green-200 p-2.5 flex items-start gap-2">
              <span className="h-2 w-2 rounded-full bg-green-600 mt-1.5 shrink-0" />
              <p className="text-green-800"><strong>Ambiente PRODUZIONE</strong>: i dati visualizzati sono quelli reali del sito pubblico.</p>
            </div>
          )}
        </div>
      </div>

      {/* AZIONI */}
      <div className="grid md:grid-cols-2 gap-3">
        <Button
          data-testid="maint-refresh"
          variant="outline"
          onClick={refresh}
          className="border-navy/20 text-navy hover:bg-sand h-10"
        >
          Aggiorna diagnosi
        </Button>

        <Button
          data-testid="maint-seed"
          variant="outline"
          disabled={busy !== null}
          onClick={() => run(
            "Seed creato",
            async () => {
              const r = await api.post("/admin/maintenance/seed-realistic");
              return `Creati ${r.data.created} lead realistici (saltati ${r.data.skipped})`;
            }
          )}
          className="border-turquoise/40 text-turquoise hover:bg-turquoise/5 h-10"
        >
          {busy === "Seed creato" ? "…" : "+ Seed 12 richieste realistiche"}
        </Button>

        <Button
          data-testid="maint-cleanup-demo"
          variant="outline"
          disabled={busy !== null}
          onClick={() => {
            if (!window.confirm("Vuoi eliminare SOLO i lead demo/sporchi (TEST, Mario Test, caicco, ecc.)? Le richieste reali resteranno.")) return;
            run(
              "Cleanup demo",
              async () => {
                const r = await api.post("/admin/maintenance/cleanup-demo");
                return `${r.data.deleted} lead demo eliminati`;
              }
            );
          }}
          className="border-orange-300 text-orange-700 hover:bg-orange-50 h-10"
        >
          {busy === "Cleanup demo" ? "…" : "Elimina solo lead demo"}
        </Button>

        <Button
          data-testid="maint-wipe-all"
          variant="outline"
          disabled={busy !== null}
          onClick={() => {
            const c1 = window.confirm("⚠️ ATTENZIONE: stai per cancellare TUTTE le richieste del database corrente.");
            if (!c1) return;
            const c2 = window.prompt("Per confermare, digita: WIPE");
            if (c2 !== "WIPE") {
              toast.error("Conferma non corretta — annullato");
              return;
            }
            run(
              "Wipe totale",
              async () => {
                const r = await api.post("/admin/maintenance/cleanup-demo?wipe_all=true");
                return `${r.data.deleted} richieste eliminate (wipe completo)`;
              }
            );
          }}
          className="border-red-300 text-red-700 hover:bg-red-50 h-10"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {busy === "Wipe totale" ? "…" : "Wipe totale archivio"}
        </Button>
      </div>

      <p className="text-[11px] text-ink/60 leading-relaxed">
        💡 <strong>Workflow consigliato</strong>: Wipe totale → Seed 12 richieste → testa la dashboard. Esegui il seed sia in preview sia in produzione (loggandoti su <code className="bg-sand px-1 rounded">{info?.public_site_url || "seamatch.it"}/admin</code> e ripetendo qui le azioni).
      </p>
    </div>
  );
}
